import React from 'react';
import { FormStepID } from '@features/onboarding/enums/formSections.enum';
import { 
  DentalIcon, 
  VisionIcon, 
  AccidentIcon, 
  CriticalIllnessIcon, 
  HospitalIcon 
} from '@components/ui/icons';

export type CategoryText = {
  id?: string,
  title: string;
  description: string;
  section?: {
    title: string;
    description: string;
  };
};

const DEFAULT_TITLE = 'Dental Plans.';
const DEFAULT_DESCRIPTION =
  'Taking care of your smile is more than just brushing and flossing—it’s about having the right coverage when you need it. That’s why we offer two dental plans designed to fit different needs and budgets. Whether you’re looking for essential coverage for routine checkups or more comprehensive protection that includes major procedures, we’ve got you covered. A good dental plan can help you stay on top of your oral health while avoiding unexpected costs.';
const sanitizeCategory = (category: string) =>
  category.toLowerCase().replace(/\s+/g, '');

const categoryTexts: Record<string, CategoryText> = {
  accident: {
    id: FormStepID.AccidentPlans,
    title: 'Accident Plan.',
    description:
      'Accidents happen—but the financial stress doesn’t have to. Our Equitable Accident plan helps cover unexpected expenses from injuries like sprains, fractures, or emergency room visits. Whether it’s a weekend mishap or a slip at home, these plans provide cash benefits that can be used however you need—medical bills, transportation, childcare, or even groceries. It’s a smart way to protect your wallet while you focus on getting better.',
    section: {
      title: 'Peace of mind for the unexpected',
      description:
        'Get cash benefits to help with unexpected costs after an accident.',
    },
  },
  'critical illness': {
    id: FormStepID.CriticalIllnessPlans,
    title: 'Critical Illness Plan.',
    description:
      'A serious diagnosis can turn life upside down—but a good plan can help you stay on your feet. Equitable Critical Illness coverage offers a lump-sum cash benefit if you’re diagnosed with a covered condition like cancer, heart attack, or stroke. You can use the money however you need—medical care, household bills, travel, or recovery support. It’s peace of mind during life’s toughest moments.',
    section: {
      title: 'A plan for potential critical health moments',
      description:
        'Receive a lump-sum payment to help with expenses during serious health events.',
    },
  },
  vision: {
    id: FormStepID.VisionPlans,
    title: 'Vision Plan.',
    description:
      'Healthy eyes start with regular care. Our Guardian Vision plan makes it easy and affordable to get the eye exams, glasses, or contacts you need. With access to a large national network of providers and great discounts on frames and lenses, you can keep your vision clear and your costs low. Whether it’s routine checkups or updating your prescription, our plans help you see the difference.',
    section: {
      title: 'A clear choice for your vision care',
      description:
        'This plan can help you save on exams, glasses, contacts, and more.',
    },
  },
  'hospital indemnity': {
    id: FormStepID.HospitalIndemnityPlans,
    title: 'Hospital Indemnity Plans.',
    description:
      'A hospital stay can come with more than just a medical bill. That’s where our Equitable Hospital Indemnity plans come in. These plans provide cash benefits for hospital admissions, overnight stays, and related services—paid directly to you. You can use the money for anything from copays and deductibles to everyday expenses. It’s an extra layer of financial protection when you need it most.',
    section: {
      title: 'Plans to help you handle hospital expenses',
      description:
        'Get financial support for expenses that a health plan might not fully cover.',
    },
  },
  dental: {
    id: FormStepID.DentalPlans,
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    section: {
      title: 'Keeping your smile healthy starts here.',
      description:
        'Here are some affordable dental plans that make it easy to keep your teeth and gums in great shape.',
    },
  },
};

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  dental: DentalIcon,
  vision: VisionIcon,
  accident: AccidentIcon,
  criticalillness: CriticalIllnessIcon,
  hospitalindemnity: HospitalIcon,
};

export const getCategoryText = (category: string): CategoryText => {
  const key = category.toLowerCase();
  return (
    categoryTexts[key] ?? {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    }
  );
};

export const getCategoryIcon = (category: string): React.ComponentType<{ className?: string; size?: number }> => {
  const key = sanitizeCategory(category);
  return CATEGORY_ICONS[key] ?? DentalIcon;
};
